import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

/**
 * Generate a PDF from a DOM element with logo + title + date in the header.
 * @param {HTMLElement} element - DOM node to capture
 * @param {Object} opts
 * @param {string} opts.title - Title to render in header
 * @param {string} [opts.subtitle] - Optional subtitle (e.g., user / district)
 * @param {string} [opts.filename] - Output filename
 */
export async function generatePDF(element, { title, subtitle = '', filename = 'reporte.pdf' }) {
  if (!element) throw new Error('Elemento no encontrado');

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#FFFFFF',
    logging: false,
    windowWidth: element.scrollWidth,
  });

  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 12;
  const headerHeight = 28;

  // ─── Header ───────────────────────────────
  // Try to load logo
  try {
    const logoUrl = `${window.location.origin}/logo.png`;
    const logoImg = await loadImage(logoUrl);
    pdf.addImage(logoImg, 'PNG', margin, 8, 14, 14);
  } catch (err) {
    console.warn('Logo no disponible para PDF:', err);
  }
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor('#0A0A0A');
  pdf.text('Ciudad Inteligente', margin + 18, 14);

  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor('#52525B');
  pdf.text(title, margin + 18, 20);

  // Date right-aligned
  const date = new Date().toLocaleString('es-MX', {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
  pdf.setFontSize(9);
  pdf.text(date, pageWidth - margin, 14, { align: 'right' });
  if (subtitle) {
    pdf.text(subtitle, pageWidth - margin, 20, { align: 'right' });
  }

  // Divider
  pdf.setDrawColor('#E4E4E7');
  pdf.setLineWidth(0.4);
  pdf.line(margin, 24, pageWidth - margin, 24);

  // ─── Body (canvas image) ──────────────────
  const imgWidth = pageWidth - margin * 2;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;
  const availableHeight = pageHeight - headerHeight - margin;

  if (imgHeight <= availableHeight) {
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, headerHeight, imgWidth, imgHeight);
  } else {
    // Multi-page: slice the canvas
    const sliceHeightPx = (availableHeight * canvas.width) / imgWidth;
    let yOffset = 0;
    let firstPage = true;
    while (yOffset < canvas.height) {
      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = canvas.width;
      sliceCanvas.height = Math.min(sliceHeightPx, canvas.height - yOffset);
      const ctx = sliceCanvas.getContext('2d');
      ctx.drawImage(
        canvas,
        0, yOffset, canvas.width, sliceCanvas.height,
        0, 0, canvas.width, sliceCanvas.height
      );
      const sliceImgHeight = (sliceCanvas.height * imgWidth) / canvas.width;
      if (!firstPage) {
        pdf.addPage();
        // re-draw mini header on continuation pages
        pdf.setFontSize(9);
        pdf.setTextColor('#52525B');
        pdf.text(`${title} — ${date}`, margin, 10);
        pdf.setDrawColor('#E4E4E7');
        pdf.line(margin, 12, pageWidth - margin, 12);
      }
      pdf.addImage(
        sliceCanvas.toDataURL('image/png'), 'PNG',
        margin,
        firstPage ? headerHeight : 16,
        imgWidth, sliceImgHeight
      );
      yOffset += sliceCanvas.height;
      firstPage = false;
    }
  }

  // Footer page numbers
  const pages = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setTextColor('#A1A1AA');
    pdf.text(`Página ${i} de ${pages}`, pageWidth - margin, pageHeight - 6, { align: 'right' });
    pdf.text('Ciudad Inteligente · Plataforma de Gestión Urbana', margin, pageHeight - 6);
  }

  pdf.save(filename);
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
